import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Input, Label, Textarea } from '@/components/ui'
import { Lock, Copy, Check, Trash2, Loader2, ThumbsUp, ThumbsDown, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { useVotes } from '@/hooks/useVotes'

export function CaptchaV3() {
  const [anchor, setAnchor] = useState('')
  const [output, setOutput] = useState('')
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState(false)
  const [showReport, setShowReport] = useState(false)
  const [reportSite, setReportSite] = useState('')
  const { votes, castVote } = useVotes('captcha-v3')

  const handleEncrypt = async () => {
    if (!anchor.trim()) {
      toast.error('Anchor is required')
      return
    }

    setLoading(true)
    try {
      const response = await fetch('https://asianprozyy.us/encrypt/inv3', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ anchor: anchor.trim() }),
      })

      const data = await response.text()
      setOutput(data)
      toast.success('Encryption complete')
    } catch (error) {
      toast.error('Encryption failed')
      setOutput('Error: ' + (error instanceof Error ? error.message : 'Unknown error'))
    } finally {
      setLoading(false)
    }
  }

  const copyToClipboard = async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
      setCopied(true)
      toast.success('Copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error('Failed to copy')
    }
  }

  const clearAll = () => {
    setAnchor('')
    setOutput('')
    toast.success('Cleared')
  }

  const submitReport = () => {
    if (!reportSite.trim()) {
      toast.error('Please provide a site URL')
      return
    }
    castVote('needs_update')
    toast.success('Report submitted. We will check and update soon.')
    setShowReport(false)
    setReportSite('')
  }

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20">
              <Lock className="h-6 w-6 text-orange-500" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Captcha v3</h1>
              <p className="text-muted-foreground">Invisible Captcha solver</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => castVote('working')}>
              <ThumbsUp className="h-4 w-4 mr-2 text-green-500" />
              {votes.working}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => castVote('not_working')}>
              <ThumbsDown className="h-4 w-4 mr-2 text-red-500" />
              {votes.not_working}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setShowReport(true)}>
              <RefreshCw className="h-4 w-4 mr-2 text-yellow-500" />
              {votes.needs_update}
            </Button>
          </div>
        </div>
      </div>

      {showReport && (
        <Card className="glass border-yellow-500/50">
          <CardContent className="pt-4">
            <div className="space-y-3">
              <p className="text-sm text-yellow-500">Please provide the site URL so we can check and update:</p>
              <Input
                value={reportSite}
                onChange={(e) => setReportSite(e.target.value)}
                placeholder="https://example.com"
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={submitReport}>Submit</Button>
                <Button size="sm" variant="outline" onClick={() => setShowReport(false)}>Cancel</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="glass">
          <CardHeader>
            <CardTitle>Input</CardTitle>
            <CardDescription>Enter Captcha parameters</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Anchor <span className="text-red-500">*</span></Label>
              <Textarea
                value={anchor}
                onChange={(e) => setAnchor(e.target.value)}
                placeholder="https://www.google.com/recaptcha/api2/anchor?ar=1&k=..."
                className="font-mono min-h-[200px]"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleEncrypt} disabled={loading} className="flex-1">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Encrypt
              </Button>
              <Button variant="outline" onClick={clearAll}>
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="glass">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Output</CardTitle>
                <CardDescription>Encrypted result</CardDescription>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={copyToClipboard}
                disabled={!output}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={output}
              readOnly
              placeholder="Encrypted output will appear here..."
              className="font-mono min-h-[200px]"
            />
          </CardContent>
        </Card>
      </div>
      <Card className="glass">
        <CardHeader>
          <CardTitle>Request an Encryptor</CardTitle>
          <CardDescription>
            Can't find the encryptor you're looking for? Request it here.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Link to="/request-encryptor">
            <Button>Request Encryptor</Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  )
}
